import { useEffect } from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  View,
} from 'react-native';
import { useRouter, type Href } from 'expo-router';

import { needsProfileSetup } from '@/lib/onboarding';
import { peekDestination } from '@/lib/pending-destination';
import { useAuthStore } from '@/state/auth-store';
import { useAppTheme } from '@/theme/app-theme';

export default function OAuthRedirect() {
  const router = useRouter();
  const { colors } = useAppTheme();
  const status = useAuthStore((state) => state.status);
  const user = useAuthStore((state) => state.user);
  const onboarding = useAuthStore(
    (state) => state.onboarding,
  );

  useEffect(() => {
    if (status === 'authenticated') {
      // Same test as the central guard. A provider hands us a display name, so
      // checking only that sent Google users straight past the profile step
      // into the permission prompts.
      const needsProfile = needsProfileSetup(user);
      const needsPermissions =
        !onboarding.permissionsDone;

      if (needsProfile) {
        router.replace('/(onboarding)/name');
        return undefined;
      }

      if (needsPermissions) {
        router.replace('/(onboarding)/permissions');
        return undefined;
      }

      // The invitation this sign-in interrupted, if there was one. The
      // central guard ignores this route on purpose (it is a transient
      // handoff), so the resume must happen HERE or an OAuth login discards
      // the invite that an OTP login would have kept. Peek, not consume —
      // the guard clears the slot on arrival.
      const pending = peekDestination();
      router.replace(pending ? (pending as Href) : '/(tabs)/home');
      return undefined;
    }

    if (status === 'loading') {
      return undefined;
    }

    const timeout = setTimeout(() => {
      router.replace('/(auth)/welcome');
    }, 8000);

    return () => clearTimeout(timeout);
  }, [
    onboarding.permissionsDone,
    router,
    status,
    user,
  ]);

  return (
    <View
      style={[
        styles.screen,
        {
          backgroundColor:
            colors.background,
        },
      ]}>
      <ActivityIndicator color={colors.primary} />
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
